import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const CurrencyContext = createContext();

// Default values for when CurrencyProvider is not mounted (public pages)
const DEFAULT_CURRENCY_CONTEXT = {
  currency: 'USD',
  symbol: '$',
  decimalPlaces: 2,
  setCurrency: () => {},
  formatAmount: (amount) => `$${Number(amount || 0).toFixed(2)}`,
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  return context || DEFAULT_CURRENCY_CONTEXT;
};

export const CURRENCIES = [
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'UGX', decimals: 0 },
  { code: 'USD', name: 'US Dollar', symbol: '$', decimals: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', decimals: 2 },
  { code: 'GBP', name: 'British Pound', symbol: '£', decimals: 2 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimals: 0 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', decimals: 2 },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', decimals: 2 },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KES', decimals: 2 },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TZS', decimals: 0 },
  { code: 'RWF', name: 'Rwandan Franc', symbol: 'RWF', decimals: 0 },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', decimals: 2 },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', decimals: 2 },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵', decimals: 2 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimals: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', decimals: 2 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimals: 2 },
  { code: 'AED', name: 'UAE Dirham', symbol: 'AED', decimals: 2 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'SAR', decimals: 2 },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'QAR', decimals: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimals: 2 },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', decimals: 2 },
  { code: 'ARS', name: 'Argentine Peso', symbol: 'AR$', decimals: 2 },
  { code: 'CLP', name: 'Chilean Peso', symbol: 'CLP', decimals: 0 },
  { code: 'COP', name: 'Colombian Peso', symbol: 'COP', decimals: 2 },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', decimals: 2 },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', decimals: 0 },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', decimals: 2 },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', decimals: 2 },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', decimals: 2 },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', decimals: 2 },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', decimals: 2 },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', decimals: 2 },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', decimals: 2 },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', decimals: 0 },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: 'PKR', decimals: 2 },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', decimals: 2 },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'LKR', decimals: 2 },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', decimals: 2 },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'MAD', decimals: 2 },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'DZD', decimals: 2 },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'TND', decimals: 3 },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'ETB', decimals: 2 },
  { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA', decimals: 0 },
  { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA', decimals: 0 },
  { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZMW', decimals: 2 },
  { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MWK', decimals: 2 },
  { code: 'BWP', name: 'Botswana Pula', symbol: 'P', decimals: 2 },
  { code: 'NAD', name: 'Namibian Dollar', symbol: 'N$', decimals: 2 },
  { code: 'SZL', name: 'Swazi Lilangeni', symbol: 'SZL', decimals: 2 },
  { code: 'LSL', name: 'Lesotho Loti', symbol: 'LSL', decimals: 2 },
  { code: 'MUR', name: 'Mauritian Rupee', symbol: 'MUR', decimals: 2 },
  { code: 'SCR', name: 'Seychellois Rupee', symbol: 'SCR', decimals: 2 },
  { code: 'MGA', name: 'Malagasy Ariary', symbol: 'MGA', decimals: 0 },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', decimals: 2 },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', decimals: 2 },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', decimals: 2 },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', decimals: 2 },
  { code: 'RON', name: 'Romanian Leu', symbol: 'RON', decimals: 2 },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'BGN', decimals: 2 },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn', decimals: 2 },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimals: 2 },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimals: 2 },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', decimals: 2 },
  { code: 'ISK', name: 'Icelandic Krona', symbol: 'kr', decimals: 0 },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', decimals: 2 },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', decimals: 2 },
  { code: 'IRR', name: 'Iranian Rial', symbol: 'IRR', decimals: 0 },
  { code: 'IQD', name: 'Iraqi Dinar', symbol: 'IQD', decimals: 3 },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'JOD', decimals: 3 },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'KWD', decimals: 3 },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BHD', decimals: 3 },
  { code: 'OMR', name: 'Omani Rial', symbol: 'OMR', decimals: 3 },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'LBP', decimals: 0 },
  { code: 'AFN', name: 'Afghan Afghani', symbol: 'AFN', decimals: 2 },
  { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸', decimals: 2 },
  { code: 'UZS', name: 'Uzbekistani Som', symbol: 'UZS', decimals: 2 },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾', decimals: 2 },
  { code: 'AMD', name: 'Armenian Dram', symbol: '֏', decimals: 2 },
  { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼', decimals: 2 },
  { code: 'BYN', name: 'Belarusian Ruble', symbol: 'Br', decimals: 2 },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', decimals: 2 },
  { code: 'MDL', name: 'Moldovan Leu', symbol: 'MDL', decimals: 2 },
  { code: 'ALL', name: 'Albanian Lek', symbol: 'ALL', decimals: 2 },
  { code: 'MKD', name: 'Macedonian Denar', symbol: 'MKD', decimals: 2 },
  { code: 'RSD', name: 'Serbian Dinar', symbol: 'RSD', decimals: 2 },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark', symbol: 'KM', decimals: 2 },
  { code: 'CRC', name: 'Costa Rican Colón', symbol: '₡', decimals: 2 },
  { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q', decimals: 2 },
  { code: 'HNL', name: 'Honduran Lempira', symbol: 'L', decimals: 2 },
  { code: 'NIO', name: 'Nicaraguan Córdoba', symbol: 'C$', decimals: 2 },
  { code: 'PAB', name: 'Panamanian Balboa', symbol: 'B/.', decimals: 2 },
  { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$', decimals: 2 },
  { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$', decimals: 2 },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar', symbol: 'TT$', decimals: 2 },
  { code: 'BBD', name: 'Barbadian Dollar', symbol: 'Bds$', decimals: 2 },
  { code: 'XCD', name: 'East Caribbean Dollar', symbol: 'EC$', decimals: 2 },
  { code: 'BZD', name: 'Belize Dollar', symbol: 'BZ$', decimals: 2 },
  { code: 'GYD', name: 'Guyanese Dollar', symbol: 'G$', decimals: 2 },
  { code: 'SRD', name: 'Surinamese Dollar', symbol: 'Sr$', decimals: 2 },
  { code: 'HTG', name: 'Haitian Gourde', symbol: 'HTG', decimals: 2 },
  { code: 'CUP', name: 'Cuban Peso', symbol: '₱', decimals: 2 },
  { code: 'BOB', name: 'Bolivian Boliviano', symbol: 'Bs.', decimals: 2 },
  { code: 'PYG', name: 'Paraguayan Guarani', symbol: '₲', decimals: 0 },
  { code: 'UYU', name: 'Uruguayan Peso', symbol: '$U', decimals: 2 },
  { code: 'VES', name: 'Venezuelan Bolívar', symbol: 'Bs.S', decimals: 2 },
];

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    // Load from localStorage or use default UGX
    const saved = localStorage.getItem('hospitalCurrency');
    return saved || 'UGX';
  });

  const [decimalPlaces, setDecimalPlaces] = useState(() => {
    const saved = localStorage.getItem('hospitalCurrencyDecimals');
    return saved !== null ? Number(saved) : 2;
  });

  useEffect(() => {
    // Save to localStorage whenever currency changes
    localStorage.setItem('hospitalCurrency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('hospitalCurrencyDecimals', String(decimalPlaces));
  }, [decimalPlaces]);

  const getCurrencyInfo = useCallback(() => {
    return CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];
  }, [currency]);

  const formatCurrency = useCallback((amount, options = {}) => {
    const {
      showSymbol = true,
      showCode = false,
      decimals,
    } = options;

    // Handle invalid amounts
    const numericAmount = Number(amount);
    if (!isFinite(numericAmount)) {
      return '-';
    }

    const currencyInfo = getCurrencyInfo();
    // If the currency itself uses 0 decimals (e.g. UGX, JPY, KRW), force 0
    // unless the caller explicitly overrides via options.decimals.
    const effectiveDecimals = decimals !== undefined ? decimals : (currencyInfo.decimals === 0 ? 0 : decimalPlaces);
    const formattedAmount = numericAmount.toLocaleString(undefined, {
      minimumFractionDigits: effectiveDecimals,
      maximumFractionDigits: effectiveDecimals,
    });

    if (showSymbol && showCode) {
      return `${currencyInfo.symbol} ${formattedAmount} ${currencyInfo.code}`;
    } else if (showSymbol) {
      return `${currencyInfo.symbol} ${formattedAmount}`;
    } else if (showCode) {
      return `${formattedAmount} ${currencyInfo.code}`;
    }
    
    return formattedAmount;
  }, [currency, decimalPlaces, getCurrencyInfo]);

  const renderCurrency = useCallback((amount, options = {}) => (
    <span className="font-financial">{formatCurrency(amount, options)}</span>
  ), [formatCurrency]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    decimalPlaces,
    setDecimalPlaces,
    getCurrencyInfo,
    formatCurrency,
    renderCurrency,
    currencies: CURRENCIES
  }), [currency, setCurrency, decimalPlaces, setDecimalPlaces, getCurrencyInfo, formatCurrency, renderCurrency]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
